import { type Option } from '@dfg/brahma-types';
import {
  FULL_MONTH_NAMES,
  MILLISECONDS_TO_YEAR,
  SHORT_MONTH_NAMES,
  DD_MM_YYYY_REGEX
} from './constants';
import { DATE_TYPE } from './dateTime.types';

// function to detemine give year is a leap year or not
export const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

// function to get days between two dates
export const getDays = (startDate: Date, endDate: Date): number => {
  const diffInTime = endDate.getTime() - startDate.getTime();
  const diffInDays = diffInTime / (1000 * 3600 * 24);
  return diffInDays;
};

// function to get percentage of days between two dates assuming dates are from some year
export const getPercentageOfDays = (
  startDate: Date,
  endDate: Date,
  totalDays: number,
  includeEndDate: boolean = true
): number => {
  const diffInDays = getDays(startDate, endDate) + (includeEndDate ? 1 : 0);
  return (diffInDays / totalDays) * 100;
};

// function to get total days in a given year
export const getTotalDaysInYear = (year: number): number => {
  return isLeapYear(year) ? 366 : 365;
};

// function to get today's date
export const getTodayDate = () => {
  const today = new Date();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const year = today.getFullYear();
  const date = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${date}`;
};

/**
 * Converting ISO string date to DD/MM/YYYY format
 */
export const isoToDateMonthYear = (isoString: string): string => {
  const date = new Date(isoString);

  // Extract day, month, and year components
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear();

  // Create the DD/MM/YYYY formatted string
  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate;
};

/**
 * Converting ISO string date to DD MonthName YYYY format
 */
export const isoToDateFullMonthYear = (isoString: string) => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return isoString;
  }

  // Extract day, month, and year components
  const day = date.getDate().toString();
  const monthName = FULL_MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();

  // Create the formatted date string
  const formattedDate = `${day} ${monthName} ${year}`;
  return formattedDate;
};

export function getDateFullMonthYearFromEpocTime(epocTime: number) {
  return isoToDateFullMonthYear(new Date(epocTime).toISOString());
}

// Convert the time stamp to date and time format (2024-01-19T15:45:00.000Z ---> 24 Dec, 2023 03:45 PM)
export function formatDateInDateAndTime(timestamp: string): string {
  const date = new Date(timestamp);
  // Return empty string for invalid date
  if (isNaN(date.getTime())) {
    return '';
  }
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
  const day = date.getDate();
  const hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const twelveHour = hours % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day} ${month}, ${date.getFullYear()} ${twelveHour}:${minutes} ${ampm}`;
}

// Converts a date string from "dd/mm/yyyy" format to "yyyy-mm-dd" format.
export function convertDateToISOFormat(dateString: string): string {
  try {
    const parts = dateString?.split('/');

    const formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;

    return formattedDate;
  } catch {
    return '';
  }
}

// Converts a date string from "yyyy-mm-dd" format to Date format.
export function convertYYYYMMDDToEpoch(dateString: string): number {
  try {
    const parts = dateString?.split('-');

    // Extract year, month, and day from the components
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-based in JavaScript Date objects
    const day = parseInt(parts[2], 10);

    return new Date(year, month, day).getTime();
  } catch (error) {
    return new Date().getTime();
  }
}

// Converts a date (in epoch) to "yyyy-mm-dd" format.
export function convertEpochToYYYYMMDD(epoch: number): string {
  const date = new Date(epoch);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function getYearsAgoDate(yearsAgo: number): number {
  const today = new Date();
  const yearsAgoDate = new Date(
    today.getFullYear() - yearsAgo,
    today.getMonth(),
    today.getDate()
  ).getTime();
  return yearsAgoDate;
}

export function diffDays(date1: Date, date2: Date): number {
  const diffTime = date2.getTime() - date1.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getMiddleDay(startDate: Date, endDate: Date) {
  // Calculate the average timestamp
  const averageTimestamp = (startDate.getTime() + endDate.getTime()) / 2;

  // Create a new Date object with the average timestamp
  const middleDate = new Date(averageTimestamp);

  return middleDate;
}

// Converts a date string from "dd/mm/yyyy" or EPOC Time or Date format to 24 April 2024 format.
export const formatDate = (date: string | number | Date) => {
  const dateString = new Date(date);
  const formattedDate = dateString.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  });

  return formattedDate;
};

export function convertToEpoch(dateString: string): number {
  const date = new Date(dateString);
  return date.getTime();
}

/* compare dates in YYYY format */
export const compareDatesInYYYY = (date1: string, date2: string) => {
  return convertToEpoch(date1) - convertToEpoch(date2);
};

// compare date in YYYY-MM-DD format
export function compareDatesInYYYYMMDD(date1: string, date2: string) {
  return convertYYYYMMDDToEpoch(date1) - convertYYYYMMDDToEpoch(date2);
}
export const compareDatesInYYYYMM = (date1: string, date2: string) => {
  return convertToEpoch(date1) - convertToEpoch(date2);
};

export function totalYearsDifference(date1: Date, date2: Date) {
  const diffTime = date2.getTime() - date1.getTime();
  const diffYears = diffTime / MILLISECONDS_TO_YEAR;
  return diffYears;
}

export const percentage = (
  startDate: Date,
  endDate: Date,
  year: number
): number => {
  return (
    100 - getPercentageOfDays(startDate, endDate, getTotalDaysInYear(year))
  );
};

export function convertEpochTimeToHumanReadbledate(timestamp: any) {
  // if(isNaN(timestamp)) return timestamp;
  let unixTimeStamp = parseInt(timestamp);
  if (timestamp.toString().length === 10) {
    unixTimeStamp = parseInt(timestamp) * 1000;
  }
  const date = new Date(unixTimeStamp);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

export function formatEpochTime(epochTime: number): string {
  const date: Date = new Date(epochTime * 1000);
  let hours: number = date.getHours();
  const minutes: number = date.getMinutes();
  const period: string = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours || 12;
  const formattedTime: string =
    hours + ':' + (minutes < 10 ? '0' : '') + minutes + ' ' + period;
  return formattedTime;
}

// Function to get timezone from date object
export function getTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export const getTimeByDateRange = (timeRange: string) => {
  const date = new Date(timeRange);
  return date.getTime();
};

export const getEndTimeForDate = (date: string) => {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toString();
};

export const convertGMTToLocal = (gmtTimestamp: string) => {
  const gmtDate = new Date(gmtTimestamp);
  const localDate = new Date(gmtDate);

  const formattedDate = localDate.toISOString()?.split('T')[0];

  const hours = localDate.getHours();
  const minutes = localDate.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedTime = `${hours % 12 || 12}:${minutes
    .toString()
    .padStart(2, '0')} ${period}`;

  return { date: formattedDate, time: formattedTime };
};

export const getCurrentDateTimeString = () => {
  return new Date()
    .toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    .replace(',', '');
};
export function convertDateTimeToEpoch(time: string, date: string): number {
  // time:12:00AM
  const [timePart, period] = time?.split(/(AM|PM)/);
  let [hour, minute] = timePart?.split(':')?.map(Number);
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;

  return new Date(
    `${date}T${hour.toString().padStart(2, '0')}:${minute
      .toString()
      .padStart(2, '0')}:00`
  ).getTime();
}

export function generateTimeOption(min?: string): Option[] {
  const currentTime = min ? new Date(min) : new Date();
  const currentTotalMinutes =
    currentTime.getHours() * 60 + currentTime.getMinutes();

  const options = Array.from({ length: 48 }, (_, i) => {
    const totalMinutes = i * 30;
    const hour24 = Math.floor(totalMinutes / 60);
    const hour12 = hour24 % 12 || 12;
    const minute = totalMinutes % 60 === 0 ? '00' : '30';
    const period = hour24 < 12 ? 'AM' : 'PM';

    if (min && totalMinutes <= currentTotalMinutes) return null;
    return {
      label: `${hour12}:${minute}${period}`,
      value: `${hour12}:${minute}${period}`
    };
  }).filter(Boolean) as Option[];

  if (min) {
    const now = new Date().getTime();
    const today = new Date();
    const localDate = `${today.getFullYear()}-${(today.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

    return options.filter(
      ({ value }) => convertDateTimeToEpoch(value as string, localDate) > now
    );
  }
  return options;
}

/* util that takes the language as argument and returns the list of months in a year */
export const generatelanguageSpecificMonthList = (lang: string = 'en') => {
  const formatter = new Intl.DateTimeFormat(lang, { month: 'short' });

  const months = Array.from({ length: 12 }, (_, i) => {
    const monthDate = new Date(0, i); // Month index starts from 0 (January)
    return { value: i + 1, label: formatter.format(monthDate) };
  });
  return months;
};

/* utility to format month and year from number and lang code */
export const generateLanguageSpecificMonthYear = (
  month: number,
  year: number,
  lang: string = 'en'
) => {
  const date = new Date(year, month - 1); // Month is zero-based in JavaScript Date

  // Format the date using Intl.DateTimeFormat
  const formattedDate = new Intl.DateTimeFormat(lang, {
    month: 'short',
    year: 'numeric'
  }).format(date);

  return formattedDate;
};

/* utility to return a list of days of the week based on the lang */
export const generateLanguageSpecificDayNames = (
  lang: string = 'en'
): string[] => {
  // Create a date formatter using the given language
  const formatter = new Intl.DateTimeFormat(lang, { weekday: 'short' });

  // Generate an array of day names starting from Sunday to Saturday
  const days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(1970, 0, 5 + i); // Start from Jan 5, 1970 (Monday)
    return formatter.format(date);
  });

  return days;
};

export interface GenerateMonthDatesWithPaddingReturnType {
  value: number;
  type: 'previous' | 'current' | 'next';
  disabled: boolean;
}

/* utility to generate a list of days with padded start and padded end with previous and next month's date */
export const generateMonthDatesWithPadding = (
  month: number,
  year: number,
  minMonth: number | null,
  minYear: number | null,
  maxMonth: number | null,
  maxYear: number | null,
  minDate: number | null,
  maxDate: number | null
): GenerateMonthDatesWithPaddingReturnType[] => {
  // Adjust month value to 0-indexed for Date object (1 = January, 12 = December)
  const firstDay = new Date(year, month - 1, 1); // Create a date object for the first day of the given month and year
  const lastDay = new Date(year, month, 0); // Create a date object for the last day of the given month and year

  const daysInMonth = lastDay.getDate(); // Get the total number of days in the current month
  const firstDayOfWeek = firstDay.getDay(); // Get the day of the week for the first day (0 = Sunday, 6 = Saturday)
  const paddedStart = (firstDayOfWeek + 6) % 7; // Calculate the number of padding days from the previous month (adjust for Monday start)

  const result: GenerateMonthDatesWithPaddingReturnType[] = []; // Initialize an array to store the result

  if (paddedStart > 0) {
    // Add previous month's dates if the first day is not a Monday
    const prevMonthLastDay = new Date(year, month - 1, 0).getDate(); // Get the total number of days in the previous month
    for (let i = paddedStart - 1; i >= 0; i--) {
      // Add the necessary dates from the previous month
      result.push({
        value: prevMonthLastDay - i,
        type: 'previous',
        disabled: true
      });
    }
  }

  for (let i = 1; i <= daysInMonth; i++) {
    // Add all dates for the current month
    const isLessThanMinDate = !!(
      minYear &&
      minMonth &&
      minDate &&
      year === minYear &&
      month === minMonth &&
      i < minDate
    );
    const isMoreThanMaxDate = !!(
      maxYear &&
      maxMonth &&
      maxDate &&
      year === maxYear &&
      month === maxMonth &&
      i > maxDate
    );
    if (isLessThanMinDate || isMoreThanMaxDate) {
      result.push({ value: i, type: 'current', disabled: true });
    } else {
      result.push({ value: i, type: 'current', disabled: false });
    }
  }

  const remainingDays = (7 - (result.length % 7)) % 7; // Calculate the number of padding days needed to complete the week
  for (let i = 1; i <= remainingDays; i++) {
    // Add the necessary dates from the next month
    result.push({ value: i, type: 'next', disabled: true });
  }

  return result; // Return the array of dates with padding
};

/* utility to generate yyyy-mm-dd or yyyy-mm or yyyy based on the arguments */
interface FormatDateFromYearMonthDate {
  year: number;
  month: number | null;
  date: number | null;
}

export const formatDateFromYearMonthDate = (
  args: FormatDateFromYearMonthDate
): string => {
  const { year, month, date } = args;

  const paddedYear = year ? year.toString().padStart(4, '0') : null;
  const paddedMonth = month ? month.toString().padStart(2, '0') : null;
  const paddedDate = date ? date.toString().padStart(2, '0') : null;

  if (year && month && date) {
    return `${paddedYear}-${paddedMonth}-${paddedDate}`;
  } else if (month && year) {
    return `${paddedYear}-${paddedMonth}`;
  } else if (year) {
    return `${year}`;
  } else {
    return '';
  }
};

/* utlity to parse the date generated by formatDateFromYearMonthDate() function */
export const parseDateString = (
  dateString: string | number | null,
  dateType?: keyof typeof DATE_TYPE
) => {
  if (dateString === null || dateString === undefined) {
    return { year: null, month: null, date: null };
  }

  let formattedDate: string = '';
  const dateObj = new Date(dateString);
  // Check if the date is valid
  if (!isNaN(dateObj.getTime())) {
    // Convert to YYYY-MM-DD format
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const date = dateObj.getDate().toString().padStart(2, '0');
    if (dateType) {
      if (dateType === DATE_TYPE.year) {
        formattedDate = `${year}`;
      } else if (dateType === DATE_TYPE.month) {
        formattedDate = `${year}-${month}`;
      } else {
        formattedDate = `${year}-${month}-${date}`;
      }
    } else formattedDate = `${year}-${month}-${date}`;
  } else {
    // If we can't parse it as a date, use the original string
    formattedDate = dateString?.toString();
  }

  // Split the input string by the hyphen
  const parts = formattedDate?.split('-')?.map(Number); // Convert parts to numbers
  const [year, month, date] = parts;

  return {
    year: year || null,
    month: month || null,
    date: date || null
  };
};

/* utility to convert a dateString into language specific date format */
export const generateLanguageSpecificDate = (
  dateString: string | null | undefined,
  lang: string = 'en',
  dateType?: keyof typeof DATE_TYPE
) => {
  if (dateString === null || dateString === undefined) return null;
  const parsedDate = parseDateString(dateString, dateType);

  const { date, month, year } = parsedDate;

  if (year && month && date) {
    // yyyy-mm-dd format
    const newDate = new Date(year, month - 1, date);
    return new Intl.DateTimeFormat(lang, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(newDate);
  } else if (year && month) {
    // yyyy-mm format
    const date = new Date(year, month - 1);
    return new Intl.DateTimeFormat(lang, {
      month: 'long',
      year: 'numeric'
    }).format(date);
  } else if (year) {
    // yyyy format
    return year.toString();
  } else {
    return '';
  }
};

/* utlity to format yyyy-mm. 2024-01 -> January 2024 */
export const formatYYYYMM = (yyyyMM: string) => {
  const date = new Date(yyyyMM);
  const formatter = new Intl.DateTimeFormat('en', {
    month: 'long',
    year: 'numeric'
  });
  return formatter.format(date);
};

export const convertToEpochStartOfDay = (dateString: string) => {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

export const convertToEpochEndOfDay = (dateString: string) => {
  const date = new Date(dateString);
  date.setHours(23, 59, 59, 999);
  return date.getTime();
};

export const isoToDateShortMonthYear = (isoString: string) => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return isoString;
  }

  // Extract day, month, and year components
  const day = date.getDate().toString();
  const monthName = SHORT_MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();

  // Create the formatted date string
  const formattedDate = `${day} ${monthName} ${year}`;
  return formattedDate;
};

/**
 * Format date with time and timezone for geotag - matches backend formatDateForGeotag exactly
 * Format: DD/MM/YYYY HH:MM:SS GMT+/-HH:MM
 */
export const formatDateWithTimeAndTimezone = (timestamp: number): string => {
  const date = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  // Calculate timezone offset
  const offsetMinutes = date.getTimezoneOffset();
  const offsetSign = offsetMinutes <= 0 ? '+' : '-';
  const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
  const offsetMins = Math.abs(offsetMinutes % 60);

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} GMT${offsetSign}${pad(
    offsetHours
  )}:${pad(offsetMins)}`;
};

/**
 * Replaces epoch timestamps (10 or 13 digit numbers) in a string with human-readable dates.
 *
 * The function searches for epoch timestamps in the format of 10-digit (seconds) or 13-digit (milliseconds)
 * Unix timestamps and replaces them with formatted dates like "15 January 2024, 3:45 PM".
 *
 * @param {string} value - The string containing epoch timestamps to be replaced
 * @returns {string} The string with all epoch timestamps replaced with human-readable dates.
 *                   If no epoch timestamps are found, returns the original string unchanged.
 *
 * @example
 * replaceEpochInString("Event occurred at 1705320000")
 * // Returns: "Event occurred at 15 January 2024, 12:00 AM"
 *
 * @example
 * replaceEpochInString("Start: 1705320000000, End: 1705406400000")
 * // Returns: "Start: 15 January 2024, 12:00 AM, End: 16 January 2024, 12:00 AM"
 */
export const replaceEpochInString = (value: string): string => {
  const epochPattern = /\b(\d{10}|\d{13})\b/g;

  // If no epoch is found → return as-is
  if (!value.match(epochPattern)) return value;

  // If epoch found → run replacement (slow path) - replace all occurrences
  return value.replace(epochPattern, (match) =>
    convertEpochTimeToHumanReadbledate(Number(match))
  );
};

/**
 * Converts various date string formats directly to ISO format (YYYY-MM-DD) for form storage.
 *
 * This utility function handles multiple input formats and converts them directly to ISO format
 * with validation to ensure the parsed date components represent a valid calendar date.
 *
 * Supported input formats:
 * - ISO format with timestamp: "1994-09-10T00:00:00.000Z" or "1994-09-10t00:00:00.000z"
 * - ISO date format: "1994-09-10"
 * - DD/MM/YYYY format: "10/09/1994" or "1/9/1994" (with or without zero-padding)
 *
 * @param dateString - Date string in various supported formats, or null/undefined
 * @returns ISO date string in YYYY-MM-DD format, or empty string for invalid inputs
 *
 * @example
 * convertToISODateFormat("1994-09-10T00:00:00.000Z") // Returns: "1994-09-10"
 * convertToISODateFormat("10/09/1994") // Returns: "1994-09-10"
 * convertToISODateFormat("1/9/1994") // Returns: "1994-09-10"
 * convertToISODateFormat("2024-02-30") // Returns: "" (invalid date)
 * convertToISODateFormat(null) // Returns: ""
 */
export const convertToISODateFormat = (
  dateString: string | null | undefined | number
): string => {
  try {
    if (!dateString) {
      return '';
    }
    let date = new Date(dateString);
    if (isNaN(date.getTime())) {
      if (DD_MM_YYYY_REGEX.test(dateString.toString())) {
        const [dd, mm, yy] = dateString.toString().split('/');
        date = new Date(+yy, +mm - 1, +dd);
      } else {
        return '';
      }
    }
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;
  } catch (error) {
    return '';
  }
};
/*
 * Formats duration in milliseconds to human-readable format.
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string (e.g., "2d 5h", "3h 45m", "10m 30s", "45s")
 */
export const formatDuration = (ms: number | null): string => {
  if (!ms) return '---';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

/**
 * Formats duration in seconds to MM:SS or HH:MM:SS format.
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "05:30" or "01:05:30")
 */
export const formatDurationFromSeconds = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Gets current local time string formatted for display.
 * @returns Formatted local time string (e.g., "03:45 PM IST")
 */
export const getLocalTime = (): string => {
  return new Date().toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

/**
 * IANA Timezone mapping for countries
 * Uses the primary/capital city timezone for each country
 * Handles DST automatically through Intl.DateTimeFormat
 */
export const COUNTRY_TIMEZONES: Record<string, string> = {
  // GCC Countries (No DST)
  AE: 'Asia/Dubai',
  SA: 'Asia/Riyadh',
  KW: 'Asia/Kuwait',
  QA: 'Asia/Qatar',
  BH: 'Asia/Bahrain',
  OM: 'Asia/Muscat',
  YE: 'Asia/Aden',
  JO: 'Asia/Amman',
  LB: 'Asia/Beirut',
  IQ: 'Asia/Baghdad',
  SY: 'Asia/Damascus',
  PS: 'Asia/Gaza',
  IL: 'Asia/Jerusalem',

  // South Asia
  IN: 'Asia/Kolkata',
  PK: 'Asia/Karachi',
  BD: 'Asia/Dhaka',
  LK: 'Asia/Colombo',
  NP: 'Asia/Kathmandu',
  AF: 'Asia/Kabul',
  BT: 'Asia/Thimphu',
  MV: 'Indian/Maldives',

  // Southeast Asia
  PH: 'Asia/Manila',
  ID: 'Asia/Jakarta',
  MY: 'Asia/Kuala_Lumpur',
  SG: 'Asia/Singapore',
  TH: 'Asia/Bangkok',
  VN: 'Asia/Ho_Chi_Minh',
  MM: 'Asia/Yangon',
  KH: 'Asia/Phnom_Penh',
  LA: 'Asia/Vientiane',
  BN: 'Asia/Brunei',
  TL: 'Asia/Dili',

  // East Asia
  CN: 'Asia/Shanghai',
  JP: 'Asia/Tokyo',
  KR: 'Asia/Seoul',
  TW: 'Asia/Taipei',
  HK: 'Asia/Hong_Kong',
  MO: 'Asia/Macau',
  MN: 'Asia/Ulaanbaatar',
  KP: 'Asia/Pyongyang',

  // Central Asia
  KZ: 'Asia/Almaty',
  UZ: 'Asia/Tashkent',
  TM: 'Asia/Ashgabat',
  TJ: 'Asia/Dushanbe',
  KG: 'Asia/Bishkek',

  // Caucasus
  GE: 'Asia/Tbilisi',
  AM: 'Asia/Yerevan',
  AZ: 'Asia/Baku',

  // Europe
  GB: 'Europe/London',
  DE: 'Europe/Berlin',
  FR: 'Europe/Paris',
  IT: 'Europe/Rome',
  ES: 'Europe/Madrid',
  PT: 'Europe/Lisbon',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  CH: 'Europe/Zurich',
  AT: 'Europe/Vienna',
  PL: 'Europe/Warsaw',
  CZ: 'Europe/Prague',
  SK: 'Europe/Bratislava',
  HU: 'Europe/Budapest',
  RO: 'Europe/Bucharest',
  BG: 'Europe/Sofia',
  GR: 'Europe/Athens',
  TR: 'Europe/Istanbul',
  UA: 'Europe/Kiev',
  RU: 'Europe/Moscow',
  BY: 'Europe/Minsk',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DK: 'Europe/Copenhagen',
  FI: 'Europe/Helsinki',
  IE: 'Europe/Dublin',
  IS: 'Atlantic/Reykjavik',
  HR: 'Europe/Zagreb',
  RS: 'Europe/Belgrade',
  SI: 'Europe/Ljubljana',
  BA: 'Europe/Sarajevo',
  ME: 'Europe/Podgorica',
  MK: 'Europe/Skopje',
  AL: 'Europe/Tirane',
  XK: 'Europe/Belgrade',
  MD: 'Europe/Chisinau',
  LT: 'Europe/Vilnius',
  LV: 'Europe/Riga',
  EE: 'Europe/Tallinn',
  CY: 'Asia/Nicosia',
  MT: 'Europe/Malta',
  LU: 'Europe/Luxembourg',
  LI: 'Europe/Vaduz',
  MC: 'Europe/Monaco',
  AD: 'Europe/Andorra',
  SM: 'Europe/San_Marino',
  VA: 'Europe/Vatican',

  // Americas
  US: 'America/New_York',
  CA: 'America/Toronto',
  MX: 'America/Mexico_City',
  BR: 'America/Sao_Paulo',
  AR: 'America/Argentina/Buenos_Aires',
  CL: 'America/Santiago',
  CO: 'America/Bogota',
  PE: 'America/Lima',
  VE: 'America/Caracas',
  EC: 'America/Guayaquil',
  BO: 'America/La_Paz',
  PY: 'America/Asuncion',
  UY: 'America/Montevideo',
  GY: 'America/Guyana',
  SR: 'America/Paramaribo',
  PA: 'America/Panama',
  CR: 'America/Costa_Rica',
  NI: 'America/Managua',
  HN: 'America/Tegucigalpa',
  SV: 'America/El_Salvador',
  GT: 'America/Guatemala',
  BZ: 'America/Belize',
  CU: 'America/Havana',
  DO: 'America/Santo_Domingo',
  HT: 'America/Port-au-Prince',
  JM: 'America/Jamaica',
  TT: 'America/Port_of_Spain',
  BB: 'America/Barbados',
  BS: 'America/Nassau',
  PR: 'America/Puerto_Rico',

  // Africa
  EG: 'Africa/Cairo',
  ZA: 'Africa/Johannesburg',
  NG: 'Africa/Lagos',
  KE: 'Africa/Nairobi',
  ET: 'Africa/Addis_Ababa',
  TZ: 'Africa/Dar_es_Salaam',
  UG: 'Africa/Kampala',
  GH: 'Africa/Accra',
  CI: 'Africa/Abidjan',
  SN: 'Africa/Dakar',
  CM: 'Africa/Douala',
  MA: 'Africa/Casablanca',
  DZ: 'Africa/Algiers',
  TN: 'Africa/Tunis',
  LY: 'Africa/Tripoli',
  SD: 'Africa/Khartoum',
  AO: 'Africa/Luanda',
  MZ: 'Africa/Maputo',
  ZW: 'Africa/Harare',
  ZM: 'Africa/Lusaka',
  BW: 'Africa/Gaborone',
  NA: 'Africa/Windhoek',
  RW: 'Africa/Kigali',
  MW: 'Africa/Blantyre',
  MG: 'Indian/Antananarivo',
  MU: 'Indian/Mauritius',
  SC: 'Indian/Mahe',

  // Oceania
  AU: 'Australia/Sydney',
  NZ: 'Pacific/Auckland',
  FJ: 'Pacific/Fiji',
  PG: 'Pacific/Port_Moresby',
  WS: 'Pacific/Apia',
  TO: 'Pacific/Tongatapu',
  VU: 'Pacific/Efate',
  SB: 'Pacific/Guadalcanal'
};

/**
 * Get local time for a specific country based on ISO country code
 * @param countryCode - ISO 2-letter country code (e.g., 'AE', 'IN')
 * @returns Formatted time string with AM/PM (e.g., '12:34 PM') or empty string if not found
 */
export const getCountryLocalTime = (countryCode?: string): string => {
  if (!countryCode) return '';

  const timezone = COUNTRY_TIMEZONES[countryCode.toUpperCase()];
  if (!timezone) return '';

  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    }).format(new Date());
  } catch {
    return '';
  }
};

export { FULL_WEEK_DAY_NAMES } from './constants';
